<template>
    <ui-modal class="missing-index-modal">
        <template #header>
            <div class="header">
                <h3 v-if="isOrderSentSuccessfully" class="h3 mb-0">
                    Świetnie!
                </h3>
                <template v-else>
                    <h3 class="h3 mb-0">
                        Brakujące indeksy
                    </h3>
                    <p class="typo--lg manufacturer-name">{{ manufacturerName }}</p>
                </template>
            </div>
        </template>
        <page-preloader
            mode="wrapper"
            class="auto-height"
            :is-loading="sendOrderStatus === 'loading'"
        >
            <template
                v-if="sendOrderStatus === 'loading'"
                #loaderDescription
            >
                <div class="p-4 text-center">
                    <h5>Trwa wysyłanie zamówienia. Nie zamykaj okna.</h5>
                </div>
            </template>
            <div
                v-if="isOrderSentSuccessfully"
                class="d-flex justify-content-center flex-column align-items-center gap-2 pt-3 pb-3"
            >
                <a-icon icon="Ok" width="40px" height="40px" />
                <div class="text-center typo--lg typo--primary">
                    <p class="mb-0">
                        Twoje zamówienie zostało wysłane do Eurocash360.
                        Znajdziesz je w zakładce "Zamówienia do zatwierdzenia".
                    </p>
                    <p class="mb-0">
                        Aby je sfinalizować, wejdź w <strong>Moje konto</strong>,
                        wybierz <strong>Historię zakupów</strong>, kliknij
                        w <strong>Tylko zamówienia do zatwierdzenia</strong>
                        i wyszukaj zamówienia pochodzące z EuroPlatform.
                    </p>
                </div>
            </div>
            <div v-else class="index-list">
                <div
                    class="typo typo--name typo--lg index-list-counter"
                >
                    Licznik brakujących indeksów: {{ indexItemsWithSelection }} /  {{ missingIndexCount }}
                    <a-icon
                        v-tooltip="indexTooltipText"
                        icon="Info"
                        width="20px"
                        height="20px"
                    />
                </div>
                <div class="d-flex justify-content-end d-lg-none select-all-checkbox-mobile">
                    <label for="selectAllIndexes">Zaznacz wszystkie</label>
                    <ui-checkbox-input
                        v-model="selectAllIndexes"
                        name="selectAllIndexes"
                        @change="handleSelectAllChange"
                    />
                </div>
                <div class="index-list-table">
                    <div class="c-table">
                        <div class="c-thead d-none d-lg-flex">
                            <div class="c-td index-options">
                                <a-icon
                                    v-tooltip="tooltipText"
                                    icon="Info"
                                    width="20px"
                                    height="20px"
                                />
                            </div>
                            <div class="c-td index-checkbox">
                                <ui-checkbox-input
                                    v-model="selectAllIndexes"
                                    name="selectAllIndexes"
                                    @change="handleSelectAllChange"
                                />
                            </div>
                            <div class="c-td index-name">Nazwa produktu</div>
                            <div
                                v-if="hasAnyIndexEanNo"
                                class="c-td index-ean"
                            >
                                Kod EAN
                            </div>
                            <div class="c-td index-sap">Kod SAP</div>
                        </div>
                        <div class="c-tbody">
                            <index-item
                                v-for="index in indexList"
                                :key="index.material_id"
                                :index="index"
                                :show-ean="hasAnyIndexEanNo"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </page-preloader>

        <template
            v-if="!isOrderSentSuccessfully"
            #footer
        >
            <div class="buttons-container">
                <the-btn
                    class="btn-outline-custom"
                    text="Zamknij"
                    @click="$emit('close-modal')"
                />
                <the-btn
                    v-tooltip="indexItemsWithSelection > 0 ?
                        '' : 'Zaznacz co najmniej jeden produkt, aby wysłać zamówienie'"
                    text="Wyślij zamówienie do Eurocash360"
                    class="btn-secondary"
                    :disabled="indexItemsWithSelection === 0"
                    :loading-status="sendOrderStatus"
                    :icon="sendOrderStatus==='loading' ? 'Spinner' : ''"
                    @click="prepareAndSendOrder"
                />
            </div>
        </template>
    </ui-modal>
</template>

<script>
import IndexItem from './IndexItem.vue'
import { mapActions, mapGetters } from 'vuex'

export default {

    components: {
        IndexItem
    },

    data() {
        return {
            selectAllIndexes: false,
            localIndexList: [],
            isOrderSentSuccessfully: false
        }
    },

    computed: {
        ...mapGetters('retrCurrent', [
            'sendOrderStatus'
        ]),
        modalArgs() {
            return this.$store.getters.modalState?.args ?? {}
        },
        indexList() {
            return this.localIndexList
        },
        manufacturerName() {
            return this.modalArgs?.manufacturerName ?? ''
        },
        hasAnyIndexEanNo() {
            return this.localIndexList.some(index => index.ean_no || index.substitute_eans?.length > 0)
        },
        indexItemsWithSelection() {
            return this.localIndexList.filter(index => {
                const mainSelected = index.selected
                const sapSelected = index.substitute_saps?.some(sub => sub.selected)
                const eanSelected = index.substitute_eans?.some(sub => sub.selected)
                return mainSelected || sapSelected || eanSelected
            }).length
        },
        allMainIndexesSelected() {
            return this.localIndexList.length > 0 &&
                this.localIndexList.every(index => index.selected)
        },
        tooltipText() {
            return 'Rozwiń produkty zamienne, tzw. zamienniki. ' +
                'Możesz je wybrać zamiast produktów głównych - ' +
                'zostaną uwzględnione w realizacji warunku kontraktu.'
        },
        conditionType() {
            return this.modalArgs?.conditionType ?? ''
        },
        missingIndexCount() {
            return this.modalArgs?.missingIndexCount ?? 0
        },
        indexTooltipText() {
            const type = this.conditionType?.toLowerCase() || ''
            return `Zamów brakujące indeksy, aby zrealizować warunek "${type}" asortymentu`
        }
    },

    created() {
        this.initializeLocalIndexList()
        // fetch locations to get extended merchant data
        this.$store.dispatch('locationsSynchronizationFetch')
    },

    watch: {
        allMainIndexesSelected(value) {
            // auto sync select all checkbox with main indexes selection
            // prevent infinite loop by checking if value changed
            if (this.selectAllIndexes !== value) {
                this.selectAllIndexes = value
            }
        }
    },

    methods: {
        ...mapActions('retrCurrent', [
            'sendOrder'
        ]),
        initializeLocalIndexList() {
            // Create single source of truth for selection state
            // Deep copy indexList and add selected field to each element (main index + substitutes)
            // This allows parent component to track all selections in one place
            const indexList = this.modalArgs?.indexList ?? []
            this.localIndexList = indexList.map(index => ({
                ...index,
                selected: false,
                substitute_saps: index.substitute_saps?.map(sub => ({
                    ...sub,
                    selected: false
                })) || [],
                substitute_eans: index.substitute_eans?.map(sub => ({
                    ...sub,
                    selected: false
                })) || []
            }))
        },
        handleSelectAllChange(value) {
            if (value) {
                // select all main indexes without substitutes
                this.localIndexList.forEach(index => {
                    this.$set(index, 'selected', true)
                })
            } else {
                // unselect all indexes with substitutes
                this.localIndexList.forEach(index => {
                    this.$set(index, 'selected', false)
                    if (index.substitute_saps) {
                        index.substitute_saps.forEach(sub => {
                            this.$set(sub, 'selected', false)
                        })
                    }
                    if (index.substitute_eans) {
                        index.substitute_eans.forEach(sub => {
                            this.$set(sub, 'selected', false)
                        })
                    }
                })
            }
        },
        async prepareAndSendOrder() {
            const products = this.localIndexList.reduce((acc, index) => {
                // Add main index if selected
                if (index.selected) {
                    acc.push({ name: index.material_name, sap: index.material_id })
                }

                // Add selected substitute_saps
                if (index.substitute_saps) {
                    const selectedSaps = index.substitute_saps
                        .filter(substitute => substitute.selected)
                        .map(substitute => ({
                            name: substitute.name,
                            sap: substitute.sap
                        }))
                    acc.push(...selectedSaps)
                }

                return acc
            }, [])

            try {
                await this.sendOrder(products)
                this.isOrderSentSuccessfully = true
            } catch (error) {
                this.$store.dispatch('addNotification', {
                    type: 'danger',
                    content: 'Wystąpił błąd. Spróbuj ponownie później.'
                })
            }
        }
    }

}
</script>

<style lang="scss">
@import '../../../styles/_custom.scss';
@import '~bootstrap/scss/mixins';

.missing-index-modal {
    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;

        .manufacturer-name {
            color: var(--color-dark);
        }

        .selection-info {
            color: var(--color-primary);
            font-weight: 600;
        }
    }

    .index-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        @include media-breakpoint-up(lg) {
           padding-top: 1.5rem
        }

        .index-list-counter {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        .index-list-table {
            padding: 0.5rem;
            @include media-breakpoint-up(lg) {
                padding: 0.5rem 1rem;
                border-radius: 5px;
                border:1px solid var(--color-primary);
            }
        }
        .select-all-checkbox-mobile {
            padding-right: 1.5rem;
            gap: 1rem;
            label {
                margin: 0;
            }
        }
        .c-table {
            .c-thead {
                display: flex;
                align-items: center;
                position: relative;
                padding-bottom: 1rem;
                min-height: 56px;
                &::after{
                    content: '';
                    position: absolute;
                    bottom: 0.5rem;
                    left: 0;
                    width: 100%;
                    height: 1px;
                    background-color: var(--color-primary);
                }

                .c-tr {
                    width: 100%;
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                }
            }

            .c-tbody {
                > *:nth-child(even) {
                    background-color: var(--color-white);
                }

                > *:nth-child(odd) {
                    background-color: var(--color-gray-100);
                }

                > *:not(:last-child) {
                    border-bottom: 1px solid var(--color-gray-200);
                }

                .c-row {
                    background-color: transparent !important;
                    border: none;
                }
            }

            .index-options {
                flex: 0 0 50px;
                display: flex;
                justify-content: flex-start;
                padding: 0;
                align-self: center;
            }

            .index-checkbox {
                flex: 0 0 50px;
                padding: 0;
            }

            .index-name {
                flex: 1;
                min-width: 200px;
                padding: 0;
            }

            .index-ean {
                flex: 0 0 150px;
                padding: 0;
            }

            .index-sap {
                flex: 0 0 150px;
                padding: 0;
            }
        }
    }
    .buttons-container {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        width: 100%;
        @include media-breakpoint-up(lg) {
            flex-direction: row;
            > * {
                flex:1;
            }
        }
    }
}

</style>